import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from "react-hot-toast";
import { UserContext } from "../../UserContext";
import styles from "./SetRole.module.css";
import { FaArrowLeft } from "react-icons/fa";

// The complete list of modules, based on your API response and images
const ALL_MODULES = [
  "Fields",
  "Tasks",
  "Tasks List",
  "Issues",
  "Inventory",
  "Products",
  "Online Support",
  "Farms",
  "Invoices",
  "Expenses",
  "Incomes",
  "Suppliers/Clients",
  "Employees",
  "Assets",
  "Assets Requests",
  "Reports",
  "Robot Data",
  "User Tracking",
  "Tracked User",
  "Daily Affairs",
  "Dashboard",
  "Notifications",
  "HR Financial",
  "Labor",
];

// Action keys derived from the API response (e.g., "view": true)
const PERMISSION_ACTIONS = ["view", "add", "edit", "delete"];

// Helper to transform API permissions into the structured state for the UI
const structurePermissions = (apiPermissions = []) => {
  const structured = {};
  ALL_MODULES.forEach((moduleName) => {
    const perm = apiPermissions.find((p) => p.module === moduleName);
    structured[moduleName] = {
      view: perm?.view || false,
      add: perm?.add || false,
      edit: perm?.edit || false,
      delete: perm?.delete || false,
      fullAccess: perm?.fullAccess || false,
    };
  });
  return structured;
};

const PermissionModule = ({
  moduleName,
  permissions,
  isEditMode,
  onPermissionChange,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // This logic handles the "Full Access" checkbox state and changes
  const allChecked = PERMISSION_ACTIONS.every((action) => permissions[action]);

  const handleFullAccessChange = (e) => {
    const { checked } = e.target;
    PERMISSION_ACTIONS.forEach((action) => {
      onPermissionChange(moduleName, action, checked);
    });
    onPermissionChange(moduleName, "fullAccess", checked);
  };

  // Renders the list of checkboxes, either enabled or disabled
  const renderPermissions = (disabled = false) => (
    <>
      <div className={styles.permissionRow}>
        <label>{t("Full Access")}</label>
        <input
          type="checkbox"
          checked={permissions.fullAccess || allChecked}
          onChange={handleFullAccessChange}
          disabled={disabled}
        />
      </div>
      {PERMISSION_ACTIONS.map((action) => (
        <div className={styles.permissionRow} key={action}>
          <label>{t(action)}</label>
          <input
            type="checkbox"
            checked={permissions[action]}
            onChange={(e) =>
              onPermissionChange(moduleName, action, e.target.checked)
            }
            disabled={disabled}
          />
        </div>
      ))}
    </>
  );

  // ** NEW: Generate summary text for collapsed read-only view **
  const readOnlySummary = PERMISSION_ACTIONS.filter(
    (action) => permissions[action]
  )
    .map((action) => t(action))
    .join(", ");

  return (
    <div
      className={`${styles.permissionModule} ${
        !isEditMode ? styles.readOnly : ""
      }`}
    >
      <div
        className={styles.permissionHeader}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.permissionTitle}>
          <span>{t(moduleName)}</span>
          {/* Show summary text only when collapsed and in read-only mode */}
          {!isEditMode && !isExpanded && (
            <span className={styles.enabledPermissions}>
              {readOnlySummary || t("No Permissions")}
            </span>
          )}
        </div>
        <i
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ""}`}
        ></i>
      </div>
      {isExpanded && (
        <div className={styles.permissionBody}>
          {renderPermissions(!isEditMode)}
        </div>
      )}
    </div>
  );
};

// The main SetRole component's logic remains the same.
const SetRole = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, token } = useContext(UserContext);
  const {
    fromPath,
    currentRoleId,
    preservedState,
  } = location.state || {};
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(currentRoleId || "");
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [customRoleNameEn, setCustomRoleNameEn] = useState("");
  const [customRoleNameAr, setCustomRoleNameAr] = useState("");
  const [permissions, setPermissions] = useState(structurePermissions());
  const selectedRole = roles.find((r) => r.id === parseInt(selectedRoleId));

  useEffect(() => {
    const fetchRoles = async () => {
      if (!userData?.Tenant?.id) return;
      try {
        const response = await axios.get(
          `/api/role/findByTenantId/${userData.Tenant.id}`,
          { headers: { Authorization: token } }
        );
        const flattenedRoles = response.data.items
          .filter((item) => item && item.Role)
          .map((item) => ({
            id: item.Role.id,
            nameEn: item.Role.nameEn,
            nameAr: item.Role.nameAr,
            tenantId: item.Role.tenantId,
            RolePermissions: item.RolePermissions,
          }));
        setRoles(flattenedRoles || []);
      } catch (error) {
        toast.error(t("Could not load roles."));
      } finally {
        setLoading(false);
      }
    };
    fetchRoles();
  }, [token, userData?.Tenant?.id, t]);
  console.log("selected role id", selectedRoleId);
  useEffect(() => {
    if (selectedRoleId === "custom") {
      setIsEditMode(true);
      setCustomRoleNameEn("");
      setCustomRoleNameAr("");
      setPermissions(structurePermissions());
    } else if (selectedRole) {
      setIsEditMode(false);
      setCustomRoleNameEn(selectedRole.nameEn);
      setCustomRoleNameAr(selectedRole.nameAr);
      setPermissions(structurePermissions(selectedRole.RolePermissions));
    }
  }, [selectedRoleId, roles]);

  const handleEditClick = () => {
    setIsEditMode(true);
    if (selectedRole && selectedRole.tenantId === null) {
      setCustomRoleNameEn("");
      setCustomRoleNameAr("");
    }
  };

  const handlePermissionChange = (module, action, value) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: { ...prev[module], [action]: value },
    }));
  };

  const handleSave = () => {
    if (isEditMode && (!customRoleNameEn || !customRoleNameAr)) {
      toast.error(t("Please enter the role name in both English and Arabic."));
      return;
    }
    const permissionsPayload = Object.entries(permissions).map(
      ([moduleName, actions]) => ({
        module: moduleName,
        view: actions.view,
        add: actions.add,
        edit: actions.edit,
        delete: actions.delete,
        fullAccess: actions.fullAccess,
      })
    );
    const roleDataToReturn = {
      roleId:
        isEditMode &&
        (selectedRoleId === "custom" || selectedRole?.tenantId === null)
          ? 0
          : parseInt(selectedRoleId),
      roleNameEn: isEditMode ? customRoleNameEn : selectedRole.nameEn,
      roleNameAr: isEditMode ? customRoleNameAr : selectedRole.nameAr,
      permissions: permissionsPayload,
    };
    navigate(fromPath || "/employees/new", {
      state: {
        preservedState,
        roleId: roleDataToReturn.roleId,
        roleData: roleDataToReturn,
      },
    });
  };

  const handleGoBack = () => {
    console.log("currentRoleId from go back", currentRoleId);
    navigate(fromPath || "/employees/new", {
      state: {
        preservedState,
        roleId: currentRoleId,
      },
    });
  };

  return (
    <div className={styles.pageContainer}>
      <header className={styles.pageHeader}>
        <button onClick={handleGoBack} className={styles.backButton}>
          <FaArrowLeft />
        </button>
        <h1>{t("Set Job Role")}</h1>
      </header>
      <main className={styles.pageContent}>
        <div className={styles.formGroup}>
          <label>{t("Job")}</label>
          <select
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            className={styles.roleSelect}
          >
            <option value="">{t("Select a Role")}</option>
            {console.log("Roles array:", roles)}
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {i18n.language === "ar" ? role.nameAr : role.nameEn}
              </option>
            ))}
            <option value="custom">{t("Custom Role")}</option>
          </select>
        </div>
        {isEditMode && (
          <div className={styles.customRoleInputs}>
            <div className={styles.formGroup}>
              <label>{t("Job in Arabic")}</label>
              <input
                type="text"
                value={customRoleNameAr}
                onChange={(e) => setCustomRoleNameAr(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label>{t("Job in English")}</label>
              <input
                type="text"
                value={customRoleNameEn}
                onChange={(e) => setCustomRoleNameEn(e.target.value)}
              />
            </div>
          </div>
        )}
        <div className={styles.permissionsHeaderContainer}>
          <h3>{t("Permissions")}</h3>
          {!isEditMode && selectedRoleId && selectedRoleId !== "custom" && (
            <button
              type="button"
              onClick={handleEditClick}
              className={styles.editButton}
            >
              {t("Edit")}
            </button>
          )}
        </div>
        <div className={styles.permissionsList}>
          {loading ? (
            <p>{t("Loading...")}</p>
          ) : (
            ALL_MODULES.map((moduleName) => (
              <PermissionModule
                key={moduleName}
                moduleName={moduleName}
                permissions={permissions[moduleName]}
                isEditMode={isEditMode}
                onPermissionChange={handlePermissionChange}
              />
            ))
          )}
        </div>
      </main>
      <footer className={styles.pageFooter}>
        <button onClick={handleSave} className={styles.saveButton}>
          {t("Save")}
        </button>
      </footer>
    </div>
  );
};

export default SetRole;
